import React, { useState, useEffect } from "react";

import { useDispatch } from 'react-redux'; // Import useDispatch
import { updateIntegrationSourceDetails } from '@/Redux/slices/integrationSlice'; // Import action using alias

import sourceRequestSchema from '@Common/data/sourceRequestSchema.json';
import sourceResponseSchema from '@Common/data/sourceResponseSchema.json';
import WebsocketIcon from "@/Common/svgicons/websocket";
import GraphQlIcon from "@/Common/svgicons/graphqlIcon";
import ODataIcon from "@/Common/svgicons/odataIcon";
import SAPIcon from "@/Common/svgicons/sapIcon";
import WorkdayIcon from "@/Common/svgicons/workdayIcon";
import ServiceNowIcon from "@/Common/svgicons/serviceNow";

const SourceDetails = ({ details, isLoading, error }) => {
  const dispatch = useDispatch();

  const [selectedSourceType, setSelecedSourceType] = useState("REST API");
  const [selectedRestAPiHttpMethod, setSelectedRestAPiHttpMethod] = useState("POST");
  const [selecedRestAPiAuthMethod, setSelecedRestAPiAuthMethod] = useState("Basic");
  const [selectedDbType, setSelectedDbType] = useState("SQl Server");

  const [showDropdownRestApiHttpMethod, setShowDropdownRestApiHttpMethod] = useState(false);
  const [showDropdownRestApiAuthMethod, setShowDropdownRestApiAuthMethod] = useState(false);
  const [showDropdownDBType, setShowDropdownDBType] = useState(false);

  const [restApiSourceUrl, setRestApiSourceUrl] = useState("");
  const [restApiSourceRequestSchema, setRestApiSourceRequestSchema] = useState("");
  const [restApiSourceResponseSchema, setRestApiSourceResponseSchema] = useState("");

  const [httpHeaders, setHttpHeaders] = useState([]);

  const [dbQueryHost, setDbQueryHost] = useState("");
  const [dbQueryPort, setDbQueryPort] = useState("");
  const [dbQueryDatabase, setDbQueryDatabase] = useState("");
  const [dbQuerySchema, setDbQuerySchema] = useState("");
  const [dbQueryUsername, setDbQueryUsername] = useState("");
  const [dbQueryPassword, setDbQueryPassword] = useState("");
  const [dbQueryQuery, setDbQueryQuery] = useState("");
  const [dbQuerySSLTSL, setDbQuerySSLTSL] = useState(false);

  useEffect(() => {
    if (details) {
      setSelecedSourceType(details.sourceDetails.sourceType || "REST API");
      setSelectedRestAPiHttpMethod(details.sourceDetails.restAPIHttpMethod || "GET");
      setSelecedRestAPiAuthMethod(details.sourceDetails.restAPIAuthType || "No Auth");
      setSelectedDbType(details.sourceDetails.sourceDBType || "SQL Server");

      setRestApiSourceUrl(details.sourceDetails.restAPISourceUrl || "");
      setRestApiSourceRequestSchema(details.sourceDetails.restAPISourceRequestSchema || "");
      setRestApiSourceResponseSchema(details.sourceDetails.restAPISourceResponseSchema || "");

      setDbQueryDatabase(details.sourceDetails.dbQueryDatabase || "");
      setDbQueryHost(details.sourceDetails.dbQueryHost || "");
      setDbQueryPort(details.sourceDetails.dbQueryPort || "");
      setDbQueryQuery(details.sourceDetails.dbQueryQuery || "");
      setDbQuerySchema(details.sourceDetails.dbQuerySchema || "");
      setDbQueryUsername(details.sourceDetails.dbQueryUsername || "");
      setDbQueryPassword(details.sourceDetails.dbQueryPassword || "");
      setDbQuerySSLTSL(details.sourceDetails.dbQuerySSLTSL || false);

      setHttpHeaders(details.sourceDetails.httpHeaders || []);
    }
  }, [details]);

  const fillSampleRequestSchema = () => {
    dispatch(updateIntegrationSourceDetails({ key: 'restAPISourceRequestSchema', value: JSON.stringify(sourceRequestSchema, null, 2) }));
  };

  const fillSampleResponseSchema = () => {
    dispatch(updateIntegrationSourceDetails({ key: 'restAPISourceResponseSchema', value: JSON.stringify(sourceResponseSchema, null, 2) }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "options") {
      setSelecedSourceType(value);
      dispatch(updateIntegrationSourceDetails({ key: 'sourceType', value }));
    } else if (name === "source_url") {
      setRestApiSourceUrl(value);
      dispatch(updateIntegrationSourceDetails({ key: 'restAPISourceUrl', value }));
    } else if (name === "request_schema") {
      setRestApiSourceRequestSchema(value);
      dispatch(updateIntegrationSourceDetails({ key: 'restAPISourceRequestSchema', value }));
    } else if (name === "response_schema") {
      setRestApiSourceResponseSchema(value);
      dispatch(updateIntegrationSourceDetails({ key: 'restAPISourceResponseSchema', value }));
    } else if (name === "dbQuertHost") {
      setDbQueryHost(value);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQueryHost', value }));
    } else if (name === "dbQueryPort") {
      setDbQueryPort(value);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQueryPort', value }));
    } else if (name === "dbQueryDatabase") {
      setDbQueryDatabase(value);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQueryDatabase', value }));
    } else if (name === "dbQuerySchema") {
      setDbQuerySchema(value);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQuerySchema', value }));
    } else if (name === "dbQueryUsername") {
      setDbQueryUsername(value);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQueryUsername', value }));
    } else if (name === "dbQueryPassword") {
      setDbQueryPassword(value);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQueryPassword', value }));
    } else if (name === "dbQueryQuery") {
      setDbQueryQuery(value);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQueryQuery', value }));
    } else if (name === "dbQuerySSLTSL") {
      setDbQuerySSLTSL(e.target.checked);
      dispatch(updateIntegrationSourceDetails({ key: 'dbQuerySSLTSL', value: e.target.checked }));
    }
  };

  const toggleDropdownRestApiHttpMethod = () => {
    setShowDropdownRestApiHttpMethod(!showDropdownRestApiHttpMethod);
  }

  const toggleDropdownRestApiAuthMethod = () => {
    setShowDropdownRestApiAuthMethod(!showDropdownRestApiAuthMethod);
  }

  const toggleDropdownDBType = () => {
    setShowDropdownDBType(!showDropdownDBType);
  }

  const onDropDownRestApiHttpMethod = (option) => {
    dispatch(updateIntegrationSourceDetails({ key: 'restAPIHttpMethod', value: option }));
    setSelectedRestAPiHttpMethod(option);
    setShowDropdownRestApiHttpMethod(!showDropdownRestApiHttpMethod);
  };

  const onDropDownRestApiAuthMethod = (option) => {
    dispatch(updateIntegrationSourceDetails({ key: 'restAPIAuthType', value: option }));
    setSelecedRestAPiAuthMethod(option);
    setShowDropdownRestApiAuthMethod(!showDropdownRestApiAuthMethod);
  };

  const onDropDownDBType = (option) => {
    dispatch(updateIntegrationSourceDetails({ key: 'sourceDBType', value: option }));
    setSelectedDbType(option);
    setShowDropdownDBType(!showDropdownDBType);
  };

  const addHttpHeader = () => {
    setHttpHeaders([...httpHeaders, { key: "", value: "" }]);
  };

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = [...httpHeaders];
    const headerToUpdate = { ...newHeaders[index] }; // Create a new object for the header

    if (field === "headerKey") {
      headerToUpdate.key = value;
    } else if (field === "headerValue") {
      headerToUpdate.value = value;
    }

    newHeaders[index] = headerToUpdate; // Replace the old header with the new one
    setHttpHeaders(newHeaders);

    dispatch(updateIntegrationSourceDetails({ key: 'httpHeaders', value: newHeaders }));
  };

  const removeHttpHeader = (index) => {
    const newHeaders = [...httpHeaders];
    newHeaders.splice(index, 1);
    setHttpHeaders(newHeaders);
    dispatch(updateIntegrationSourceDetails({ key: 'httpHeaders', value: newHeaders }));
  };

  return (
    <div className="mb-6 pt-4 -ml-8">
      <div className="grid grid-cols-1 gap-4 gap-y-2 text-sm lg:grid-cols-3">
        <div className="text-white">
          
          <div className="flex">
            <div className="mx-auto w-full pr-4">
              <div className="mx-auto max-w-md space-y-6">
                <div className="space-y-4">
                  <p className="text-white text-sm font-medium">
                    Select a option
                  </p>
                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="option1-checkbox"
                      value="REST API"
                      className="peer hidden"
                      checked={selectedSourceType === "REST API"}
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="option1-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <svg
                          className="h-auto w-16"
                          viewBox="0 0 512 512"
                          fill="#000000"
                        >
                          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                          <g
                            id="SVGRepo_tracerCarrier"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></g>
                          <g id="SVGRepo_iconCarrier">
                            <g>
                              {" "}
                              <path
                                style={{ fill: "#B0B6BB" }}
                                d="M308.145,406.069c-1.836,5.12-6.691,8.828-12.42,8.828h-70.621c-5.729,0-10.593-3.708-12.429-8.828 H5.102c-2.825,0-5.102,2.286-5.102,5.102v25.106c0,2.816,2.278,5.102,5.102,5.102h501.795c2.816,0,5.102-2.286,5.102-5.102v-25.106 c0-2.816-2.286-5.102-5.102-5.102H308.145z"
                              ></path>{" "}
                              <path
                                style={{ fill: "#E8EDEE" }}
                                d="M212.678,406.069c1.836,5.12,6.691,8.828,12.42,8.828h70.621c5.729,0,10.593-3.708,12.429-8.828 H494.34V97.103c0-14.627-11.855-26.483-26.483-26.483H44.133c-14.627,0-26.483,11.855-26.483,26.483v308.966H212.678z"
                              ></path>{" "}
                              <polygon
                                style={{ fill: "#51bee8" }}
                                points="52.963,370.759 459.032,370.759 459.032,105.931 52.963,105.931 "
                              ></polygon>{" "}
                              <g>
                                {" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M105.928,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C114.756,278.528,110.801,282.483,105.928,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M158.894,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C167.721,278.528,163.767,282.483,158.894,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M220.687,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C229.515,278.528,225.56,282.483,220.687,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M158.894,247.172h-52.966c-4.873,0-8.828-3.955-8.828-8.828s3.955-8.828,8.828-8.828h52.966 c4.873,0,8.828,3.955,8.828,8.828S163.767,247.172,158.894,247.172"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M247.17,211.862h-52.966c-4.873,0-8.828-3.955-8.828-8.828c0-4.873,3.955-8.828,8.828-8.828h52.966 c4.873,0,8.828,3.955,8.828,8.828C255.997,207.907,252.043,211.862,247.17,211.862"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M308.963,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C317.79,278.528,313.836,282.483,308.963,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M335.446,211.862H282.48c-4.873,0-8.828-3.955-8.828-8.828c0-4.873,3.955-8.828,8.828-8.828h52.966 c4.873,0,8.828,3.955,8.828,8.828C344.273,207.907,340.318,211.862,335.446,211.862"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M379.584,233.931h11.034c6.082,0,11.034-4.952,11.034-11.034s-4.952-11.034-11.034-11.034h-11.034 V233.931z M370.756,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828h19.862 c15.819,0,28.69,12.871,28.69,28.69s-12.871,28.69-28.69,28.69h-11.034v22.069C379.584,278.528,375.629,282.483,370.756,282.483 L370.756,282.483z"
                                ></path>
                              </g>{" "}
                            </g>{" "}
                          </g>
                        </svg>
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            Rest API
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data from a Rest API
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="bulkapi-checkbox"
                      value="BULK API"
                      className="peer hidden"
                      checked={selectedSourceType === "BULK API"}
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="bulkapi-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <svg
                          className="h-auto w-16"
                          viewBox="0 0 512 512"
                          fill="#000000"
                        >
                          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                          <g
                            id="SVGRepo_tracerCarrier"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></g>
                          <g id="SVGRepo_iconCarrier">
                            <g>
                              {" "}
                              <path
                                style={{ fill: "#B0B6BB" }}
                                d="M308.145,406.069c-1.836,5.12-6.691,8.828-12.42,8.828h-70.621c-5.729,0-10.593-3.708-12.429-8.828 H5.102c-2.825,0-5.102,2.286-5.102,5.102v25.106c0,2.816,2.278,5.102,5.102,5.102h501.795c2.816,0,5.102-2.286,5.102-5.102v-25.106 c0-2.816-2.286-5.102-5.102-5.102H308.145z"
                              ></path>{" "}
                              <path
                                style={{ fill: "#E8EDEE" }}
                                d="M212.678,406.069c1.836,5.12,6.691,8.828,12.42,8.828h70.621c5.729,0,10.593-3.708,12.429-8.828 H494.34V97.103c0-14.627-11.855-26.483-26.483-26.483H44.133c-14.627,0-26.483,11.855-26.483,26.483v308.966H212.678z"
                              ></path>{" "}
                              <polygon
                                style={{ fill: "#51bee8" }}
                                points="52.963,370.759 459.032,370.759 459.032,105.931 52.963,105.931 "
                              ></polygon>{" "}
                              <g>
                                {" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M105.928,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C114.756,278.528,110.801,282.483,105.928,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M158.894,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C167.721,278.528,163.767,282.483,158.894,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M220.687,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C229.515,278.528,225.56,282.483,220.687,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M158.894,247.172h-52.966c-4.873,0-8.828-3.955-8.828-8.828s3.955-8.828,8.828-8.828h52.966 c4.873,0,8.828,3.955,8.828,8.828S163.767,247.172,158.894,247.172"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M247.17,211.862h-52.966c-4.873,0-8.828-3.955-8.828-8.828c0-4.873,3.955-8.828,8.828-8.828h52.966 c4.873,0,8.828,3.955,8.828,8.828C255.997,207.907,252.043,211.862,247.17,211.862"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M308.963,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828 c4.873,0,8.828,3.955,8.828,8.828v70.621C317.79,278.528,313.836,282.483,308.963,282.483"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M335.446,211.862H282.48c-4.873,0-8.828-3.955-8.828-8.828c0-4.873,3.955-8.828,8.828-8.828h52.966 c4.873,0,8.828,3.955,8.828,8.828C344.273,207.907,340.318,211.862,335.446,211.862"
                                ></path>{" "}
                                <path
                                  style={{ fill: "#FFFFFF" }}
                                  d="M379.584,233.931h11.034c6.082,0,11.034-4.952,11.034-11.034s-4.952-11.034-11.034-11.034h-11.034 V233.931z M370.756,282.483c-4.873,0-8.828-3.955-8.828-8.828v-70.621c0-4.873,3.955-8.828,8.828-8.828h19.862 c15.819,0,28.69,12.871,28.69,28.69s-12.871,28.69-28.69,28.69h-11.034v22.069C379.584,278.528,375.629,282.483,370.756,282.483 L370.756,282.483z"
                                ></path>
                              </g>{" "}
                            </g>{" "}
                          </g>
                        </svg>
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            Bulk API
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data from a Bulk API
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>


                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="ODATA-checkbox"
                      value="ODATA"
                      className="peer hidden"
                      checked={selectedSourceType === "ODATA"}
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="ODATA-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <ODataIcon />
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            OData
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data using OData
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="GRAPHQL-checkbox"
                      value="GRAPHQL"
                      className="peer hidden"
                      checked={selectedSourceType === "GRAPHQL"}
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="GRAPHQL-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <GraphQlIcon />
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            GraphQL
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data from a GraphQL endpoint
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="option2-checkbox"
                      value="DB QUERY"
                      className="peer hidden"
                      checked={selectedSourceType === "DB QUERY"} // Control with state
                      onChange={handleInputChange} // Update state on change
                    />
                    <label
                      htmlFor="option2-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <svg
                          className="h-auto w-16"
                          viewBox="-1.61 0 70 70"
                          fill="#000000"
                        >
                          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                          <g
                            id="SVGRepo_tracerCarrier"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></g>
                          <g id="SVGRepo_iconCarrier">
                            {" "}
                            <path
                              d="m -68.256041,544.14439 c -3.2225,-0.64097 -6.0047,-2.84478 -7.3573,-5.82793 -0.595,-1.31236 -0.8482,-2.55217 -0.8438,-4.13316 0.012,-4.5438 2.8692,-8.33798 7.3079,-9.70579 0.8177,-0.25196 1.3573,-0.31847 2.611,-0.32182 0.8692,-0.002 1.8277,0.0437 2.1301,0.10227 l 0.5497,0.10649 0.6765,-1.31365 c 1.3815,-2.68242 3.5345,-4.78981 6.3178,-6.18379 2.2961,-1.15 3.739,-1.48443 6.4043,-1.48443 1.8668,0 2.2999,0.045 3.5043,0.36412 5.2011,1.37813 9.255,5.45998 10.5309,10.60353 0.3195,1.2879 0.4789,4.17568 0.293,5.30849 l -0.1269,0.77355 0.7284,0.163 c 1.8256,0.40854 3.4011,1.68396 4.2066,3.40532 0.7966,1.70226 0.6508,3.90495 -0.3651,5.51414 -0.5185,0.82136 -1.535,1.73424 -2.3832,2.14031 -0.4348,0.20813 -0.8979,0.43475 -1.0292,0.50361 -0.2947,0.15465 -32.3751,0.14085 -33.155,-0.0143 z m 33.351,-1.89876 c 0.9026,-0.44879 1.7953,-1.45217 2.1662,-2.43497 0.7697,-2.03891 -0.2571,-4.40792 -2.3055,-5.31934 -0.5173,-0.23017 -0.9037,-0.28342 -1.9824,-0.27321 -0.737,0.007 -1.3399,-0.0395 -1.3399,-0.10328 0,-0.0638 0.1369,-0.66194 0.3042,-1.32926 0.428,-1.70717 0.4237,-4.42927 -0.01,-6.09178 -0.9556,-3.66584 -3.4332,-6.72357 -6.8221,-8.4192 -1.4892,-0.74512 -2.9056,-1.08594 -4.841,-1.16493 -2.8639,-0.11687 -4.8633,0.33445 -7.0666,1.59507 -2.5693,1.47003 -4.784,4.13385 -5.6425,6.78661 -0.1651,0.51018 -0.3653,0.9268 -0.4449,0.92583 -0.08,-9.6e-4 -0.5467,-0.13408 -1.038,-0.29579 -1.3668,-0.44991 -3.6978,-0.42255 -5.0619,0.0594 -1.3731,0.48513 -2.3616,1.10694 -3.3908,2.13292 -1.5427,1.53788 -2.3996,3.63391 -2.3996,5.86959 0,3.32007 1.8288,6.23059 4.8041,7.64574 1.71,0.81331 1.1183,0.78892 18.4849,0.76186 l 15.941,-0.0248 0.6444,-0.32043 z m -45.9938,-6.42576 c -5.7671,-0.78155 -10.2061,-2.22963 -13.4896,-4.40056 -1.3548,-0.89576 -2.2902,-1.89766 -2.8176,-3.01787 l -0.3929,-0.83466 0.036,-22.59585 0.036,-22.59585 0.3281,-0.61562 c 0.8294,-1.55619 2.4465,-2.88407 5.0211,-4.12288 3.2554,-1.56646 7.0095,-2.54578 12.2408,-3.19328 2.1543,-0.26664 9.47,-0.21948 11.6809,0.0753 8.1415,1.08546 14.4594,3.74551 16.4734,6.93577 0.7936,1.25717 0.7731,0.80148 0.7731,17.22791 l 0,15.08135 -0.4466,0.0692 c -0.2456,0.038 -0.8021,0.10712 -1.2366,0.15352 -4.4834,0.47873 -9.1329,3.57818 -11.5162,7.67699 l -0.488,0.8391 -1.9169,0.0905 c -2.265,0.10697 -3.4191,0.39711 -5.1411,1.29243 -2.9567,1.53725 -5.0699,4.20232 -5.9316,7.48077 -0.238,0.90554 -0.3084,1.56192 -0.3132,2.92023 l -0.01,1.75213 -0.7594,-0.0162 c -0.4176,-0.009 -1.3777,-0.1 -2.1335,-0.20243 z m 11.7239,-20.8271 c -1.0818,-0.91805 -2.0174,-1.7137 -2.0791,-1.7681 -0.062,-0.0544 0.1631,-0.2602 0.4994,-0.45733 0.7458,-0.43708 1.7061,-1.48313 2.1139,-2.30274 0.9965,-2.00287 0.9743,-4.49403 -0.058,-6.47634 -1.135,-2.1802 -3.5103,-3.3239 -6.2014,-2.986 -1.4735,0.18503 -2.3966,0.59832 -3.3701,1.50899 -1.5366,1.4374 -2.0688,2.84774 -1.988,5.26837 0.046,1.37622 0.095,1.62372 0.4886,2.45901 0.8471,1.79787 2.6182,3.14163 4.4225,3.35531 0.6788,0.0804 0.7377,0.1249 2.0865,1.57538 l 1.3881,1.49264 2.3321,0 2.3322,0 -1.9669,-1.66919 z m -5.885,-4.6866 c -0.9451,-0.47951 -1.304,-1.30103 -1.304,-2.98527 0,-1.09589 0.053,-1.41584 0.3224,-1.96387 0.4309,-0.875 1.29,-1.40105 2.1328,-1.30604 0.829,0.0934 1.2599,0.42875 1.6984,1.32149 0.3327,0.67762 0.3808,0.93439 0.3769,2.01347 0,1.33108 -0.2403,2.01354 -0.9098,2.63622 -0.6994,0.65058 -1.42,0.73892 -2.3167,0.284 z m -9.5093,3.18974 c 2.485,-0.57319 3.8348,-2.65434 3.1192,-4.80889 -0.3603,-1.0849 -1.1219,-1.76252 -2.9819,-2.65327 -0.859,-0.41138 -1.6272,-0.83198 -1.7071,-0.93468 -0.3128,-0.40184 -0.3121,-0.66784 0,-1.03351 0.484,-0.56269 1.5858,-0.5896 3.0776,-0.0752 l 1.138,0.39245 -0.039,-1.51877 -0.039,-1.51878 -0.7558,-0.17965 c -1.143,-0.2717 -3.6136,-0.2584 -4.5856,0.0247 -1.949,0.56765 -2.976,1.84152 -2.9696,3.68323 0.01,1.89537 0.8605,2.79385 3.8676,4.06981 1.2722,0.53982 1.4179,1.54865 0.2807,1.94362 -0.5263,0.18278 -0.6761,0.17922 -1.5089,-0.0358 -0.5086,-0.13132 -1.2802,-0.42314 -1.7148,-0.64849 l -0.7902,-0.40974 0,1.64418 0,1.64418 0.7902,0.21996 c 0.4346,0.12098 0.852,0.2415 0.9276,0.26783 0.3141,0.10941 3.3435,0.0524 3.8879,-0.0732 z m 26.5512,-1.4376 0,-1.44294 -2.1988,0 -2.1987,0 0,-4.74107 0,-4.74107 -1.7865,0 -1.7865,0 0,6.18401 0,6.184 3.9853,0 3.9852,0 0,-1.44293 z m -10.5815,-24.74151 c 3.5835,-0.41532 6.5241,-1.16648 8.3827,-2.14132 1.2211,-0.64044 1.5139,-0.87116 1.9833,-1.56285 1.4515,-2.13894 -1.7987,-4.28958 -8.0986,-5.35876 -5.9187,-1.0045 -13.7186,-0.77727 -18.7108,0.54509 -10.3005,2.7285 -6.2399,7.74058 7.0717,8.7287 2.1847,0.16217 7.109,0.0514 9.3717,-0.21086 z"
                              fill="#00bcf2"
                              transform="translate(97.599 -474.268)"
                            ></path>{" "}
                          </g>
                        </svg>
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            DB Query
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch data from a database
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="salesforce-checkbox"
                      value="Salesforce"
                      className="peer hidden"
                      checked={selectedSourceType === "Salesforce"} // Control with state
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="salesforce-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <svg className="h-auto w-16" viewBox="0.5 -149.38 999 999" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill="#00A1E0" d="M416.224 76.763c32.219-33.57 77.074-54.391 126.682-54.391 65.946 0 123.48 36.772 154.12 91.361 26.626-11.896 56.098-18.514 87.106-18.514 118.94 0 215.368 97.268 215.368 217.247 0 119.993-96.428 217.261-215.368 217.261a213.735 213.735 0 0 1-42.422-4.227c-26.981 48.128-78.397 80.646-137.412 80.646-24.705 0-48.072-5.706-68.877-15.853-27.352 64.337-91.077 109.448-165.348 109.448-77.344 0-143.261-48.939-168.563-117.574-11.057 2.348-22.513 3.572-34.268 3.572C75.155 585.74.5 510.317.5 417.262c0-62.359 33.542-116.807 83.378-145.937-10.26-23.608-15.967-49.665-15.967-77.06C67.911 87.25 154.79.5 261.948.5c62.914 0 118.827 29.913 154.276 76.263"></path><path fill="#FFF" d="M145.196 363.11c-.626 1.637.228 1.979.427 2.263 1.878 1.366 3.786 2.349 5.707 3.444 10.189 5.407 19.81 6.986 29.871 6.986 20.492 0 33.214-10.9 33.214-28.447v-.341c0-16.224-14.358-22.115-27.835-26.37l-1.75-.569c-10.161-3.302-18.927-6.147-18.927-12.836v-.355c0-5.721 5.123-9.934 13.064-9.934 8.823 0 19.297 2.932 26.042 6.66 0 0 1.978 1.281 2.704-.64.398-1.025 3.814-10.218 4.17-11.214.384-1.082-.299-1.879-.996-2.306-7.699-4.682-18.344-7.884-29.358-7.884l-2.049.014c-18.756 0-31.848 11.328-31.848 27.565v.342c0 17.119 14.444 22.669 27.978 26.54l2.177.669c9.862 3.031 18.358 5.635 18.358 12.58v.342c0 6.347-5.521 11.071-14.43 11.071-3.458 0-14.487-.071-26.398-7.6-1.438-.84-2.277-1.451-3.387-2.12-.583-.37-2.049-1.011-2.689.925l-4.045 11.215zM445.194 363.11c-.626 1.637.228 1.979.427 2.263 1.878 1.366 3.786 2.349 5.706 3.444 10.189 5.407 19.811 6.986 29.871 6.986 20.492 0 33.215-10.9 33.215-28.447v-.341c0-16.224-14.359-22.115-27.836-26.37l-1.75-.569c-10.161-3.302-18.928-6.147-18.928-12.836v-.355c0-5.721 5.123-9.934 13.064-9.934 8.823 0 19.297 2.932 26.043 6.66 0 0 1.978 1.281 2.703-.64.398-1.025 3.814-10.218 4.17-11.214.385-1.082-.299-1.879-.996-2.306-7.699-4.682-18.344-7.884-29.358-7.884l-2.05.014c-18.756 0-31.848 11.328-31.848 27.565v.342c0 17.119 14.444 22.669 27.978 26.54l2.177.669c9.862 3.031 18.373 5.635 18.373 12.58v.342c0 6.347-5.536 11.071-14.445 11.071-3.457 0-14.486-.071-26.397-7.6-1.438-.84-2.291-1.423-3.372-2.12-.371-.242-2.107-.911-2.705.925l-4.042 11.215zM649.995 328.74c0 9.919-1.85 17.731-5.493 23.253-3.601 5.465-9.051 8.126-16.649 8.126-7.613 0-13.035-2.647-16.579-8.126-3.587-5.507-5.407-13.334-5.407-23.253 0-9.904 1.82-17.703 5.407-23.168 3.544-5.407 8.966-8.04 16.579-8.04 7.599 0 13.049 2.633 16.664 8.04 3.629 5.464 5.478 13.263 5.478 23.168m17.106-18.386c-1.68-5.679-4.298-10.688-7.784-14.857-3.487-4.184-7.898-7.542-13.136-9.99-5.223-2.433-11.398-3.671-18.328-3.671-6.945 0-13.121 1.238-18.344 3.671-5.237 2.448-9.648 5.807-13.149 9.99-3.472 4.184-6.091 9.193-7.784 14.857-1.665 5.649-2.505 11.825-2.505 18.386s.84 12.751 2.505 18.386c1.693 5.664 4.298 10.674 7.799 14.857 3.486 4.184 7.912 7.528 13.135 9.904 5.236 2.377 11.398 3.586 18.344 3.586 6.93 0 13.092-1.209 18.328-3.586 5.223-2.376 9.648-5.721 13.136-9.904 3.486-4.17 6.104-9.179 7.784-14.857 1.68-5.649 2.519-11.84 2.519-18.386s-.841-12.737-2.52-18.386M807.568 357.47c-.569-1.665-2.177-1.039-2.177-1.039-2.49.954-5.138 1.836-7.955 2.277-2.861.44-6.006.669-9.379.669-8.281 0-14.856-2.462-19.566-7.329-4.725-4.867-7.372-12.736-7.344-23.381.029-9.691 2.362-16.978 6.561-22.527 4.17-5.521 10.517-8.354 18.984-8.354 7.059 0 12.438.811 18.072 2.59 0 0 1.352.583 1.992-1.181 1.494-4.156 2.604-7.13 4.198-11.698.456-1.295-.654-1.85-1.053-2.007-2.22-.868-7.457-2.276-11.413-2.874-3.7-.569-8.026-.868-12.836-.868-7.188 0-13.591 1.224-19.069 3.672-5.465 2.433-10.104 5.791-13.775 9.976-3.672 4.184-6.461 9.192-8.325 14.856-1.85 5.649-2.789 11.854-2.789 18.415 0 14.188 3.828 25.657 11.385 34.054 7.57 8.425 18.941 12.708 33.77 12.708 8.766 0 17.76-1.778 24.221-4.326 0 0 1.238-.598.697-2.034l-4.199-11.599zM837.497 319.238c.811-5.507 2.334-10.09 4.682-13.661 3.544-5.422 8.951-8.396 16.551-8.396s12.623 2.988 16.223 8.396c2.391 3.571 3.43 8.354 3.843 13.661h-41.299zm57.592-12.111c-1.451-5.479-5.052-11.015-7.414-13.548-3.729-4.013-7.371-6.816-10.986-8.382-4.725-2.021-10.389-3.358-16.593-3.358-7.229 0-13.79 1.21-19.112 3.714-5.336 2.505-9.818 5.921-13.334 10.176-3.516 4.24-6.162 9.292-7.842 15.027-1.693 5.707-2.547 11.926-2.547 18.485 0 6.675.883 12.894 2.633 18.486 1.765 5.636 4.582 10.602 8.396 14.714 3.799 4.142 8.695 7.387 14.558 9.648 5.821 2.249 12.894 3.416 21.019 3.401 16.722-.057 25.53-3.785 29.159-5.792.641-.355 1.253-.981.483-2.774l-3.785-10.603c-.568-1.579-2.177-.996-2.177-.996-4.142 1.537-10.032 4.298-23.766 4.27-8.979-.014-15.64-2.661-19.81-6.803-4.283-4.24-6.375-10.474-6.745-19.268l57.905.057s1.522-.028 1.68-1.509c.057-.624 1.993-11.895-1.722-24.945M373.762 319.238c.825-5.507 2.334-10.09 4.682-13.661 3.543-5.422 8.951-8.396 16.55-8.396s12.623 2.988 16.237 8.396c2.376 3.571 3.415 8.354 3.828 13.661h-41.297zm57.577-12.111c-1.451-5.479-5.037-11.015-7.399-13.548-3.729-4.013-7.372-6.816-10.986-8.382-4.725-2.021-10.388-3.358-16.593-3.358-7.215 0-13.79 1.21-19.112 3.714-5.336 2.505-9.819 5.921-13.334 10.176-3.515 4.24-6.162 9.292-7.841 15.027-1.679 5.707-2.547 11.926-2.547 18.485 0 6.675.882 12.894 2.633 18.486 1.765 5.636 4.583 10.602 8.396 14.714 3.8 4.142 8.695 7.387 14.558 9.648 5.821 2.249 12.893 3.416 21.019 3.401 16.721-.057 25.53-3.785 29.159-5.792.641-.355 1.252-.981.484-2.774l-3.771-10.603c-.584-1.579-2.191-.996-2.191-.996-4.141 1.537-10.019 4.298-23.78 4.27-8.965-.014-15.625-2.661-19.795-6.803-4.284-4.24-6.375-10.474-6.746-19.268l57.905.057s1.522-.028 1.679-1.509c.055-.624 1.99-11.895-1.738-24.945M248.601 357.153c-2.263-1.808-2.576-2.263-3.344-3.43-1.139-1.779-1.722-4.312-1.722-7.528 0-5.095 1.679-8.752 5.166-11.214-.042.015 4.981-4.34 16.792-4.184 8.296.114 15.71 1.338 15.71 1.338v26.327h.014s-7.357 1.579-15.639 2.077c-11.783.712-17.02-3.4-16.977-3.386m23.039-40.686c-2.348-.171-5.394-.271-9.037-.271-4.966 0-9.762.626-14.259 1.836-4.525 1.209-8.595 3.103-12.096 5.606a27.927 27.927 0 0 0-8.396 9.549c-2.049 3.814-3.088 8.311-3.088 13.349 0 5.123.882 9.577 2.647 13.221 1.765 3.657 4.312 6.702 7.556 9.051 3.216 2.348 7.187 4.069 11.797 5.108 4.54 1.039 9.691 1.565 15.327 1.565 5.934 0 11.854-.483 17.589-1.466 5.678-.968 12.651-2.377 14.586-2.817a146.25 146.25 0 0 0 4.056-1.039c1.438-.355 1.324-1.893 1.324-1.893l-.029-52.952c0-11.613-3.102-20.223-9.207-25.559-6.077-5.322-15.028-8.013-26.597-8.013-4.341 0-11.328.599-15.512 1.438 0 0-12.651 2.448-17.86 6.518 0 0-1.138.712-.512 2.306l4.099 11.015c.512 1.423 1.893.939 1.893.939s.441-.171.954-.47c11.143-6.062 25.231-5.877 25.231-5.877 6.262 0 11.072 1.252 14.316 3.742 3.159 2.419 4.767 6.076 4.767 13.789v2.448c-4.981-.711-9.549-1.123-9.549-1.123M738.669 286.631c.44-1.31-.484-1.936-.869-2.078-.981-.384-5.905-1.423-9.705-1.665-7.271-.441-11.312.783-14.928 2.405-3.586 1.622-7.57 4.24-9.791 7.215v-7.044c0-.982-.697-1.765-1.665-1.765h-14.843c-.967 0-1.664.782-1.664 1.765v86.366c0 .968.797 1.765 1.764 1.765h15.213a1.76 1.76 0 0 0 1.75-1.765v-43.147c0-5.792.641-11.569 1.922-15.198 1.252-3.587 2.96-6.461 5.066-8.525 2.12-2.049 4.525-3.486 7.158-4.297 2.689-.826 5.663-1.096 7.77-1.096 3.031 0 6.361.782 6.361.782 1.109.128 1.736-.555 2.105-1.565.997-2.647 3.815-10.574 4.356-12.153"></path><path fill="#FFF" d="M595.874 246.603c-1.85-.569-3.529-.954-5.721-1.366-2.221-.398-4.867-.598-7.869-.598-10.475 0-18.729 2.96-24.52 8.794-5.764 5.807-9.678 14.644-11.642 26.271l-.712 3.913h-13.148s-1.594-.057-1.936 1.68l-2.148 12.053c-.157 1.139.342 1.864 1.878 1.864h12.794l-12.979 72.463c-1.011 5.835-2.178 10.631-3.473 14.273-1.267 3.587-2.504 6.276-4.041 8.24-1.48 1.879-2.875 3.273-5.295 4.084-1.992.669-4.297.982-6.816.982-1.395 0-3.258-.229-4.639-.513-1.366-.271-2.092-.569-3.131-1.011 0 0-1.494-.568-2.092.926-.47 1.238-3.885 10.615-4.298 11.769-.398 1.152.171 2.049.896 2.319 1.708.598 2.974.996 5.294 1.551 3.217.755 5.934.797 8.481.797 5.322 0 10.189-.754 14.217-2.205 4.042-1.466 7.57-4.014 10.701-7.457 3.373-3.729 5.493-7.628 7.515-12.964 2.006-5.266 3.729-11.812 5.094-19.439l13.05-73.815h19.069s1.607.057 1.936-1.693l2.162-12.039c.143-1.152-.341-1.864-1.893-1.864h-18.514c.1-.412.939-6.931 3.06-13.063.911-2.604 2.618-4.725 4.056-6.177 1.424-1.423 3.06-2.433 4.854-3.017 1.835-.598 3.928-.882 6.219-.882 1.736 0 3.457.199 4.752.469 1.793.385 2.49.584 2.961.727 1.893.569 2.148.014 2.519-.896l4.426-12.153c.455-1.312-.669-1.867-1.067-2.023M337.194 371.834c0 .968-.697 1.751-1.665 1.751h-15.355c-.968 0-1.651-.783-1.651-1.751v-123.58c0-.967.683-1.75 1.651-1.75h15.355c.968 0 1.665.783 1.665 1.75v123.58z"></path></g></svg>

                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            Salesforce
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data from Salesforce
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="ServiceNow-checkbox"
                      value="ServiceNow"
                      className="peer hidden"
                      checked={selectedSourceType === "ServiceNow"} // Control with state
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="ServiceNow-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <ServiceNowIcon />
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            ServiceNow
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data from ServiceNow
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="Workday-checkbox"
                      value="Workday"
                      className="peer hidden"
                      checked={selectedSourceType === "Workday"} // Control with state
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="Workday-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <WorkdayIcon />
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            Workday
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data from Workday
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="SAP-checkbox"
                      value="SAP"
                      className="peer hidden"
                      checked={selectedSourceType === "SAP"} // Control with state
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="SAP-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <SAPIcon />
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            SAP
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Fetch the data from SAP
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="soapapi-checkbox"
                      value="SOAP-API"
                      className="peer hidden"
                      checked={selectedSourceType === "SOAP-API"} // Control with state
                      onChange={handleInputChange}
                    />
                    <label
                      htmlFor="soapapi-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <svg className="h-auto w-16" height="200px" width="200px" version="1.1" id="Layer_1" viewBox="0 0 512 512" fill="#000000"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path style={{ fill: '#E2E5E7' }} d="M128,0c-17.6,0-32,14.4-32,32v448c0,17.6,14.4,32,32,32h320c17.6,0,32-14.4,32-32V128L352,0H128z"></path> <path style={{ fill: '#B0B7BD' }} d="M384,128h96L352,0v96C352,113.6,366.4,128,384,128z"></path> <polygon style={{ fill: '#CAD1D8' }} points="480,224 384,128 480,128 "></polygon> <path style={{ fill: '#50BEE8' }} d="M416,416c0,8.8-7.2,16-16,16H48c-8.8,0-16-7.2-16-16V256c0-8.8,7.2-16,16-16h352c8.8,0,16,7.2,16,16 V416z"></path> <g> <path style={{ fill: '#FFFFFF' }} d="M131.28,326.176l22.272-27.888c6.64-8.688,19.568,2.432,12.288,10.752 c-7.664,9.088-15.728,18.944-23.424,29.024l26.112,32.496c7.024,9.6-7.04,18.816-13.952,9.344l-23.536-30.192l-23.152,30.832 c-6.528,9.328-20.992-1.152-13.68-9.856l25.712-32.624c-8.064-10.096-15.872-19.936-23.664-29.024 c-8.064-9.6,6.912-19.44,12.784-10.48L131.28,326.176z"></path> <path style={{ fill: '#FFFFFF' }} d="M201.264,327.84v47.328c0,5.648-4.608,8.832-9.2,8.832c-4.096,0-7.68-3.184-7.68-8.832v-72.016 c0-6.656,5.648-8.848,7.68-8.848c3.696,0,5.872,2.192,8.048,4.624l28.16,37.984l29.152-39.408c4.24-5.232,14.592-3.2,14.592,5.648 v72.016c0,5.648-3.584,8.832-7.664,8.832c-4.608,0-8.192-3.184-8.192-8.832V327.84l-21.248,26.864 c-4.592,5.648-10.352,5.648-14.576,0L201.264,327.84z"></path> <path style={{ fill: '#FFFFFF' }} d="M294.288,303.152c0-4.224,3.584-7.808,8.064-7.808c4.096,0,7.552,3.6,7.552,7.808v64.096h34.8 c12.528,0,12.8,16.752,0,16.752h-42.336c-4.48,0-8.064-3.184-8.064-7.808v-73.04H294.288z"></path> </g> <path style={{ fill: '#CAD1D8' }} d="M400,432H96v16h304c8.8,0,16-7.2,16-16v-16C416,424.8,408.8,432,400,432z"></path> </g></svg>
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            SOAP API
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Send the data to Salesforce
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                  
                  <div className="relative">
                    <input
                      type="radio"
                      name="options"
                      id="option3-checkbox"
                      value="NONE"
                      className="peer hidden"
                      checked={selectedSourceType === "NONE"} // Control with state
                      onChange={handleInputChange} // Update state on change
                    />
                    <label
                      htmlFor="option3-checkbox"
                      className="bg-white border-neutral-200/70 text-neutral-600 peer-checked:text-neutral-900 hover:text-neutral-900 hover:border-neutral-300 group inline-flex w-full cursor-pointer items-center justify-between rounded-lg border-2 p-5 peer-checked:border-blue-400 peer-checked:bg-blue-200/50"
                    >
                      <div className="flex items-center space-x-5">
                        <svg
                          className="h-auto w-16"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                          <g
                            id="SVGRepo_tracerCarrier"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          ></g>
                          <g id="SVGRepo_iconCarrier">
                            {" "}
                            <g id="Warning / Stop_Sign">
                              {" "}
                              <path
                                id="Vector"
                                d="M5.75 5.75L18.25 18.25M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21Z"
                                stroke="#000000"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              ></path>{" "}
                            </g>{" "}
                          </g>
                        </svg>
                        <div className="flex flex-col justify-start">
                          <div className="w-full text-lg font-semibold">
                            Not Required
                          </div>
                          <div className="w-full text-sm opacity-60">
                            Dont fetch any data from any source
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>


                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 text-white">
        <div className="text-lg font-medium">Source Details</div>
          <div className="mb-6">
            Provide the source details to fetch the data when this integration
            triggered.
          </div>

          {selectedSourceType == "REST API" && (
            <div className="grid grid-cols-1 gap-4 gap-y-2 text-sm md:grid-cols-5">
              <div className="md:col-span-5">
                <label htmlFor="source_url">Source Url</label>
                <input
                  type="text"
                  name="source_url"
                  id="source_url"
                  className="mt-1 h-10 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  placeholder="Url of the Source"
                  value={restApiSourceUrl}
                  onChange={handleInputChange}
                />
              </div>

              <div className="md:col-span-5">
                <div>
                  <label htmlFor="http_method">Http Method</label>
                </div>
                <div className="relative mt-2 inline-block w-64 text-left">
                  <button
                    onClick={toggleDropdownRestApiHttpMethod}
                    className="bg-white dark:border-slate-700 inline-flex w-full items-center justify-between rounded border border-gray-200 px-4 py-2 text-gray-700 dark:bg-[#20293A] dark:text-gray-400"
                  >
                    <span>{selectedRestAPiHttpMethod}</span>

                    <svg
                      className="ml-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </button>
                  {showDropdownRestApiHttpMethod && (
                    <div className="opacity-1 bg-white dark:border-slate-700 absolute z-40 mt-0 w-full rounded-md border border-gray-200 bg-white-500 shadow-lg dark:bg-[#20293A]">
                      <div
                        className="py-1 text-sm text-gray-700 dark:text-gray-400"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                      >
                        <a
                          onClick={() => onDropDownRestApiHttpMethod('GET')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          GET
                        </a>
                        <a
                          onClick={() => onDropDownRestApiHttpMethod('POST')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          POST
                        </a>
                        <a
                          onClick={() => onDropDownRestApiHttpMethod('PATCH')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          PATCH
                        </a>
                        <a
                          onClick={() => onDropDownRestApiHttpMethod('DELETE')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          DELETE
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedRestAPiHttpMethod == "GET" && (
                <div className="md:col-span-5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="descr">Headers</label>
                    <button onClick={addHttpHeader} className="rounded-md p-2 text-blue-700 hover:bg-gray-200">+ Add</button>
                  </div>
                  <div className="relative mt-2 inline-block w-full text-left">
                    {httpHeaders.length === 0 && (
                      <div className="rounded-md bg-gray-100 p-2">No headers added</div>
                    )}
                    {httpHeaders.map((header, index) => (
                      <div key={index} className="mb-2 flex items-center">
                        <input
                          type="text"
                          placeholder="Header Key"
                          className="mr-2 mt-1 h-10 w-1/3 text-gray-900 rounded border bg-gray-50 px-4"
                          value={header.key}
                          onChange={(e) => handleHeaderChange(index, "headerKey", e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Header Value"
                          className="mr-2 mt-1 h-10 w-1/3 text-gray-900 rounded border bg-gray-50 px-4"
                          value={header.value}
                          onChange={(e) => handleHeaderChange(index, "headerValue", e.target.value)}
                        />
                        <button
                          onClick={() => removeHttpHeader(index)}
                          className="rounded-md p-2 text-red-700 hover:bg-gray-200"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* <div className="md:col-span-5">
                <div>
                  <label htmlFor="auth_type">Authorization Type</label>
                </div>
                <div className="relative mt-2 inline-block w-64 text-left">
                  <button
                    onClick={toggleDropdownRestApiAuthMethod}
                    className="bg-white dark:border-slate-700 inline-flex w-full items-center justify-between rounded border border-gray-200 px-4 py-2 text-gray-700 dark:bg-[#20293A] dark:text-gray-400"
                  >
                    <span>{selecedRestAPiAuthMethod}</span>

                    <svg
                      className="ml-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </button>
                  {showDropdownRestApiAuthMethod && (
                    <div className="opacity-1 bg-white dark:border-slate-700 absolute z-40 mt-0 w-full rounded-md border border-gray-200 bg-white-500 shadow-lg dark:bg-[#20293A]">
                      <div
                        className="py-1 text-sm text-gray-700 dark:text-gray-400"
                        role="menu"
                        aria-orientation="vertical"
                        aria-labelledby="options-menu"
                      >
                        <a
                          onClick={() => onDropDownRestApiAuthMethod('No Auth')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          No auth
                        </a>
                        <a
                          onClick={() => onDropDownRestApiAuthMethod('API Key')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          API Key
                        </a>
                        <a
                          onClick={() => onDropDownRestApiAuthMethod('Bearer Token')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          Bearer Token
                        </a>
                        <a
                          onClick={() => onDropDownRestApiAuthMethod('Basic Auth')}
                          href="#"
                          className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                          role="menuitem"
                        >
                          Basic Auth
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div> */}

              {selectedRestAPiHttpMethod != "GET" && (
                <div className="md:col-span-5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="descr">Request Payload Schema</label>
                    <button onClick={fillSampleRequestSchema} className="rounded-md p-2 text-blue-700 hover:bg-gray-200">Use a sample</button>
                  </div>
                  <textarea
                    type="textarea"
                    rows={10}
                    name="request_schema"
                    id="request_schema"
                    className="mt-1 h-40 w-full text-gray-900 rounded border bg-gray-50 px-4"
                    value={restApiSourceRequestSchema}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              <div className="md:col-span-5">
                <div className="flex items-center justify-between">
                  <label htmlFor="resp">Response Schema</label>
                  <button onClick={fillSampleResponseSchema} className="rounded-md p-2 text-blue-700 hover:bg-gray-200">Use a sample</button>
                </div>
                <textarea
                  type="textarea"
                  rows={10}
                  name="response_schema"
                  id="response_schema"
                  className="mt-1 h-40 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  value={restApiSourceResponseSchema}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          )}

          {selectedSourceType === "DB QUERY" && (
            <div className="grid grid-cols-1 gap-4 gap-y-2 text-sm md:grid-cols-5">
              <div className="md:col-span-5">
                <label htmlFor="dbType">Database Type</label>
                <div className="md:col-span-5">
                  <div className="relative mt-2 inline-block w-64 text-left">
                    <button
                      onClick={toggleDropdownDBType}
                      className="bg-white dark:border-slate-700 inline-flex w-full items-center justify-between rounded border border-gray-200 px-4 py-2 text-gray-700 dark:bg-[#20293A] dark:text-gray-400"
                    >
                      <span>{selectedDbType}</span>

                      <svg
                        className="ml-2 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    </button>
                    {showDropdownDBType && (
                      <div className="opacity-1 bg-white dark:border-slate-700 absolute z-40 mt-0 w-full rounded-md border border-gray-200 bg-white-500 shadow-lg dark:bg-[#20293A]">
                        <div
                          className="py-1 text-sm text-gray-700 dark:text-gray-400"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="options-menu"
                        >
                          <a
                            onClick={() => onDropDownDBType('SQL Server')}
                            href="#"
                            className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                            role="menuitem"
                          >
                            SQL Server
                          </a>
                          <a
                            onClick={() => onDropDownDBType('MySQL')}
                            href="#"
                            className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                            role="menuitem"
                          >
                            MySQL
                          </a>
                          <a
                            onClick={() => onDropDownDBType('PostgreSQL')}
                            href="#"
                            className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                            role="menuitem"
                          >
                            PostgreSQL
                          </a>
                          <a
                            onClick={() => onDropDownDBType('MongoDB')}
                            href="#"
                            className="block px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#161d2a]"
                            role="menuitem"
                          >
                            MongoDB
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="md:col-span-3">
                <label htmlFor="dbQuertHost">Host/Server</label>
                <input
                  type="text"
                  name="dbQuertHost"
                  id="dbQuertHost"
                  className="mt-1 h-10 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  placeholder="e.g., db.example.com or 192.168.1.100"
                  value={dbQueryHost}
                  onChange={handleInputChange}
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="dbPort">Port</label>
                <input
                  type="number"
                  name="dbQueryPort"
                  id="dbQueryPort"
                  className="mt-1 h-10 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  placeholder="e.g., 3306"
                  value={dbQueryPort}
                  onChange={handleInputChange}
                />
              </div>
              <div className="md:col-span-5">
                <label htmlFor="dbName">Database Name</label>
                <input
                  type="text"
                  name="dbQueryDatabase"
                  id="dbQueryDatabase"
                  className="mt-1 h-10 w-full rtext-gray-900 ounded border bg-gray-50 px-4"
                  placeholder="e.g., mydatabase"
                  value={dbQueryDatabase}
                  onChange={handleInputChange}
                />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="dbUser">Username</label>
                <input
                  type="text"
                  name="dbQueryUsername"
                  id="dbQueryUsername"
                  className="mt-1 h-10 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  placeholder="e.g., admin"
                  value={dbQueryUsername}
                  onChange={handleInputChange}
                />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="dbPassword">Password</label>
                <input
                  type="password"
                  name="dbQueryPassword"
                  id="dbQueryPassword"
                  className="mt-1 h-10 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  placeholder="********"
                  value={dbQueryPassword}
                  onChange={handleInputChange}
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="dbSchema">Schema</label>
                <input
                  type="text"
                  name="dbQuerySchema"
                  id="dbQuerySchema"
                  className="mt-1 h-10 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  placeholder="e.g., public"
                  value={dbQuerySchema}
                  onChange={handleInputChange}
                />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="dbSSL">SSL/TLS</label>
                <input
                  type="checkbox"
                  name="dbQuerySSLTSL"
                  id="dbQuerySSLTSL"
                  className="ml-4 mt-1 text-gray-900 rounded border bg-gray-50"
                  checked={dbQuerySSLTSL}
                  onChange={handleInputChange}
                />
              </div>

              <div className="md:col-span-5">
                <label htmlFor="dbName">Query</label>
                <input
                  type="text"
                  name="dbQueryQuery"
                  id="dbQueryQuery"
                  className="mt-1 h-10 w-full text-gray-900 rounded border bg-gray-50 px-4"
                  placeholder="e.g., Select * from Orders"
                  value={dbQueryQuery}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          )}

          {selectedSourceType == "NONE" && (
            <div className="m-1 flex items-center justify-center rounded-md border border-yellow-200 bg-yellow-100 px-2 py-1 font-medium text-yellow-700">
              <div slot="avatar">
                <svg
                  width="100%"
                  height="100%"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-info mx-2 h-5 w-5"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>
              <div className="max-w-full flex-initial text-xl font-normal">
                <div className="py-2">
                  Dont fetch any data from Source
                  <div className="font-base text-sm">
                    This option will use the integration trigger payload
                  </div>
                </div>
              </div>
              <div className="flex flex-auto flex-row-reverse">

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceDetails;
